import React, { useState, Suspense, lazy, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Buttons,
  COMPONENTS_LessonCom,
  DOG_ANIMATIONS,
  GLOBAL_IMAGES,
} from "../../assets/asset";
import loading from "../../assets/global/loading.json";
import Lottie from "lottie-react";

const Checkboxtype = lazy(() => import("../QuestionTypes/Checkboxtype"));
const ImageMCQ = lazy(() => import("../QuestionTypes/ImageMCQ"));
const HorizontalMCQ = lazy(() => import("../QuestionTypes/HorizontalMCQ"));
const Shapetype = lazy(() => import("../QuestionTypes/Shapetype"));
const Entertext = lazy(() => import("../QuestionTypes/Entertext"));
const Dots = lazy(() => import("../QuestionTypes/Dots"));
const VerticalImageMcq = lazy(() =>
  import("../QuestionTypes/VerticalImageMCQ")
);
const EvenOddtype = lazy(() => import("../QuestionTypes/EvenOddtype"));
const Orderdrop = lazy(() => import("../QuestionTypes/Orderdrop/Orderdrop"));
const Slidertype = lazy(() => import("../QuestionTypes/Slidertype"));
const TextAndSequence = lazy(() => import("../QuestionTypes/TextAndSequence"));
const Reflection = lazy(() => import("../QuestionTypes/Reflection"));
const ReplaceValues = lazy(() => import("../QuestionTypes/ReplaceValues"));
const MatchType = lazy(() => import("../QuestionTypes/MatchType"));
const Sortbox = lazy(() => import("../QuestionTypes/Sortbox"));
const DetailedMCQ = lazy(() => import("../QuestionTypes/DetailedMCQ"));
const Multipletext = lazy(() => import("../QuestionTypes/Multipletext"));
const Coordinateplane = lazy(() =>
  import("../QuestionTypes/Coordinateplane/Coordinateplane")
);
const Pointsplot = lazy(() => import("../QuestionTypes/Pointsplot"));
const BarGraph = lazy(() => import("../QuestionTypes/BarGraph"));
const MultipleSelectImg = lazy(() =>
  import("../QuestionTypes/MultipleSelectImg")
);
const ColorCircle = lazy(() => import("../QuestionTypes/ColorCircle"));
const ColorSquare = lazy(() => import("../QuestionTypes/ColorSquare"));

const TextMCQ = (props) => {
  const validQuestionImages =
    props.questionData?.questionimagelist?.filter(
      (item) => item && item !== "."
    ) || [];

  const ResolvedComponent =
    validQuestionImages.length > 1 ? Shapetype : HorizontalMCQ;

  return <ResolvedComponent {...props} />;
};

const componentMap = {
  Shapetype: Shapetype, //  y6 - c1 - q1
  Checkboxtype: Checkboxtype, //  y4 - c1 - q1
  ImageMCQ: ImageMCQ, //  y4 - c1 - q6  || foundation L4  Q4
  EvenOddtype: EvenOddtype, // y4 - c-T5 - q10
  TextAndSequence: TextAndSequence, // y4 - c-10 - q5 //UI done // handle image and text in api data question -----
  Slidertype: Slidertype, // y5 - c-10 - q1
  Reflection: Reflection, // y5 - c-22 - q1
  MatchType: MatchType, //Y-foundation -  C2 - q7
  HorizontalMCQ: HorizontalMCQ, // y5 - c1 - q6
  Entertext: Entertext, // y4 - c5 - q1
  Dots: Dots, // y4 - c9 - q5 ?? c9 q5
  Orderdrop: Orderdrop, // y4 - c4 - q5
  VerticalImageMcq: VerticalImageMcq, // y4 - c-T4 - q3

  //7-9
  ReplaceValues: ReplaceValues, // Y7 - L2 - Q8

  Sortbox: Sortbox, // Y7 - L4 - Q5
  DetailedMCQ: DetailedMCQ, // Y7 - L17 - Q2 and // Y7 - L18 - Q1 (with image)
  Multipletext: Multipletext, // Y8 - L6 - Q4
  Coordinateplane: Coordinateplane, // Y8 - L9 - Q4
  BarGraph: BarGraph, // Y9 - L20 - Q2
  Pointsplot: Pointsplot, // Y8 - L24 - Q6
  MultipleSelectImg: MultipleSelectImg, // dont include this
  TextMCQ: TextMCQ, // Backend sometimes sends text-option MCQs under this type
  ColorCircle: ColorCircle,
  ColorSquare: ColorSquare,

  //! others:
  // 35 days
  // DrawText: DrawText,
  // Txt: Txt, //puzzle type
  // Video: Video,
  // Image: Image,
  // TF: TF,
  // MCQ: MCQ,
  // SelectNum: SelectNum,
  // SelectImg: SelectImg,
  // Counting: Counting,
  // AppleDrop: AppleDrop,
  // Circldrop: Circldrop,
  // balloondrop: balloondrop,
  // clock: clock,
  // Calenderdatetype: Calenderdatetype,
  // Calendermonthtype: Calendermonthtype,
  // flowerdrop: flowerdrop,
  // Noquestionselect: Noquestionselect,
  // ColorCircle: ColorCircle,
  // ColorSquare: ColorSquare,
  // Colorrectangle: Colorrectangle,
  // Imageorderdrop: Imageorderdrop,
  // facetype: facetype,
  // Choosebigger: Choosebigger,
  // Seasontype: Seasontype,
  // Cointype: Cointype,
  // Balance: Balance,
  // VerticalTextMCQ: VerticalTextMCQ,
  // Dice: Dice,
  // DrageAndWrite: DrageAndWrite,
  // AnswerText: AnswerText,
  // DrageAndDrop: DrageAndDrop,
  // ListenAndIDentify: ListenAndIDentify,
  // PatternMatching: PatternMatching,
  // CardFlip: CardFlip,
};

const resolveQuestionType = (fields = {}) => {
  const questionTypeOriginal = String(fields?.questionTypeOriginal || "")
    .trim()
    .toLowerCase();

  if (questionTypeOriginal === "colorcircle") {
    return "ColorCircle";
  }
  if (
    questionTypeOriginal === "colorsquare" ||
    questionTypeOriginal === "colorrectangle"
  ) {
    return "ColorSquare";
  }

  return fields?.questionType;
};

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  useEffect(() => {
    const handleError = (error, errorInfo) => {
      setHasError(true);
      setErrorInfo(errorInfo);
      console.error("Error caught by ErrorBoundary:", error, errorInfo);
    };

    return () => {
      setHasError(false);
      setErrorInfo(null);
    };
  }, []);

  if (hasError) {
    return (
      <div className="p-4 bg-red-50 border border-red-300 rounded-md">
        <h2 className="text-xl font-bold text-red-700 mb-2">
          Something went wrong
        </h2>
        <p className="text-red-600">
          The question component couldn't be loaded properly.
        </p>
        <p className="mt-2 text-sm text-gray-700">
          Please try refreshing the page or contact support if the issue
          persists.
        </p>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div>
          <Lottie animationData={loading} />
        </div>
      }
    >
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          onError: (error) => {
            setHasError(true);
            console.error("Component error:", error);
          },
        })
      )}
    </Suspense>
  );
};

const CallAllTypeFromHere = ({
  lessonData = [],
  yearId,
  video,
  colorTheme,
  lessonId,
  lessonDisplayName,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [attempts, setAttempts] = useState({});
  const [timeTaken, setTimeTaken] = useState({});
  const [questionKey, setQuestionKey] = useState(0);
  const [error, setError] = useState(null);
  const [isLastQuestionCompleted, setIsLastQuestionCompleted] = useState(false);
  const navigate = useNavigate();
  const [randomDogAnimation, setRandomDogAnimation] = useState(null);
  const [yearCompletionStatus, setYearCompletionStatus] = useState(0);
  const numericYearId = Number(yearId);
  const yearLabel = Number.isFinite(numericYearId) && numericYearId > 0
    ? numericYearId === 1
      ? "Foundation"
      : numericYearId - 1
    : "--";
  console.log(
    "yearCompletionStatus in call all types from here: ",
    +yearCompletionStatus
  );

  const getRandomDog = () => {
    const randomNumber = Math.floor(Math.random() * 12) + 1;
    return DOG_ANIMATIONS[`dog${randomNumber}`];
  };

  useEffect(() => {
    setQuestionKey(`${currentQuestionIndex}-${Date.now()}`);
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (isLastQuestionCompleted) {
      callStudentAnswerSecondApi();
      setRandomDogAnimation(getRandomDog());
      console.log("randomDogAnimation", randomDogAnimation);
    }
  }, [isLastQuestionCompleted]);

  useEffect(() => {
    setError(null);
  }, [currentQuestionIndex]);

  const handleNextQuestion = (
    isCorrect,
    attemptCount = 1,
    questionTime = 0
  ) => {
    try {
      const currentQuestion = lessonData[currentQuestionIndex];
      if (!currentQuestion) {
        setError("Current question is undefined");
        return;
      }

      const questionId = currentQuestion.pk?.toString();
      if (!questionId) {
        setError("Question ID is undefined");
        return;
      }

      setAttempts((prevAttempts) => ({
        ...prevAttempts,
        [questionId]: attemptCount,
      }));

      setTimeTaken((prevTimeTaken) => ({
        ...prevTimeTaken,
        [questionId]: parseFloat(questionTime.toFixed(3)),
      }));

      if (currentQuestionIndex < lessonData?.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setIsLastQuestionCompleted(true);
      }
    } catch (err) {
      setError(`Error processing question: ${err.message}`);
      console.error("Error in handleNextQuestion:", err);
    }
  };

  const handleSkip = () => {
    try {
      const currentQuestion = lessonData[currentQuestionIndex];
      if (!currentQuestion) {
        setError("Current question is undefined");
        return;
      }

      const questionId = currentQuestion.pk?.toString();
      if (!questionId) {
        setError("Question ID is undefined");
        return;
      }

      setAttempts((prevAttempts) => ({
        ...prevAttempts,
        [questionId]: 1,
      }));

      const estimatedTime = 1.0;
      setTimeTaken((prevTimeTaken) => ({
        ...prevTimeTaken,
        [questionId]: estimatedTime,
      }));

      if (currentQuestionIndex < lessonData?.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        setIsLastQuestionCompleted(true);
      }
    } catch (err) {
      setError(`Error skipping question: ${err.message}`);
      console.error("Error in handleSkip:", err);
    }
  };

  const callStudentAnswerSecondApi = async () => {
    try {
      const formattedAttempts = {};
      const formattedTimeTaken = {};

      Object.entries(attempts).forEach(([key, value]) => {
        formattedAttempts[key.toString()] = Number(value);
      });

      Object.entries(timeTaken).forEach(([key, value]) => {
        formattedTimeTaken[key.toString()] = Number(value);
      });

      const payload = {
        Attempts: formattedAttempts,
        LessonId: currentQuestion?.fields?.lession?.toString(),
        TimeTaken: formattedTimeTaken,
        YearId: yearId?.toString(),
      };

      const tokenWithQuotes = localStorage.getItem("token");
      const token = tokenWithQuotes ? JSON.parse(tokenWithQuotes) : null;

      if (!token) {
        setError("No token found. Please log in again.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/studentAnswerSecond`,
        {
          method: "POST",
          headers: {
            Authorization: `${process.env.REACT_APP_AUTH_TOKEN_DEV}`,
            "Content-Type": "application/json",
            jwt: token,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (
        result?.status === "Fail" &&
        (result?.status_code === 304 ||
          result?.message === "jwt token is not valid")
      ) {
        navigate("/login", { replace: true });
        return;
      }
      // console.log("studentAnswerSecond API response :", result);

      const attemptValue = result?.data?.[0]?.fields?.attempt ?? "0";
      setYearCompletionStatus(attemptValue);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status}, Details: ${errorText}`
        );
      }
      setError(null);
    } catch (err) {
      setError(`Failed to submit answers: ${err.message}`);
      console.error("API Error:", err);
    }
  };

  const currentQuestion = lessonData[currentQuestionIndex];
  const QuestionComponent = currentQuestion
    ? componentMap[resolveQuestionType(currentQuestion.fields)]
    : null;

  console.log("QuestionType:", resolveQuestionType(currentQuestion.fields));

  if (!currentQuestion) {
    return <div>No questions available.</div>;
  }

  if (isLastQuestionCompleted)
    return (
      <div className="flex items-center justify-center min-h-screen min-w-screen bg-transparent">
        <div className="relative flex flex-col items-center w-full max-w-[57vw] scale-[0.6] origin-center md:scale-[0.7] lg:scale-[0.8] xl:scale-[0.9] transition-all duration-300">
          <img
            src={GLOBAL_IMAGES.boardImage}
            className="w-full max-w-xs sm:max-w-md md:max-w-4xl h-auto object-contain transition-all duration-300"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-start m-10 px-2">
            <h1 className="font-mali text-xs sm:text-base  lg:text-4xl md:text-3xl  font-bold mt-4 text-dark-brown text-center">
              Awesome Job, Little Explorer
            </h1>
            <Lottie
              animationData={randomDogAnimation || DOG_ANIMATIONS.dog1}
              className="size-60 mt-4 sm:mt-8"
            />
          </div>
          <img
            onClick={() =>
              navigate("/lessonDashboard", {
                state: { yearId, yearCompletionStatus },
              })
            }
            src={Buttons.letsGo}
            className="absolute left-1/2 -translate-x-1/2 bottom-10 cursor-pointer object-contain transition-transform duration-300 w-auto h-6 sm:w-14 sm:h-8 md:w-auto md:h-16 hover:scale-100"
          />
        </div>
      </div>
    );

  return (
    <div className="flex flex-col w-screen h-screen items-center justify-between">
      {/* Header Section */}
      <div className="w-full  bg-gray-500 bg-clip-padding backdrop-filter  backdrop-blur bg-opacity-10 backdrop-saturate-100 backdrop-contrast-100 px-14 py-4 flex items-center justify-between">
        {/* Back Button */}
        <img
          src={COMPONENTS_LessonCom.back}
          alt="Back"
          className="cursor-pointer w-14 h-14"
          onClick={() => navigate(-1)}
        />

        {/* Year Display */}
        <div className="text-black text-2xl font-mali font-bold ml-10">
          Year {yearLabel}
        </div>

        {/* Progress Bar Container */}
        <div className="flex-1 mx-20">
          <div className="bg-gray-200   h-2 rounded">
            <div
              className="bg-blue-500 h-full transition-all duration-300 rounded-l rounded-r"
              style={{
                width: `${
                  ((currentQuestionIndex + 1) / lessonData?.length) * 100
                }%`,
              }}
            ></div>
          </div>
        </div>

        {/* Question Counter */}
        <div className="text-balck font-mali text-2xl font-bold">
          {currentQuestionIndex + 1}/{lessonData?.length}
        </div>
        <div className="text-black text-lg font-mali font-bold ml-10">
          Lesson {lessonDisplayName}
        </div>

        {/* Skip Button */}
        <button
          className="ml-4 px-4 py-2 bg-gray-100 text-gray-600 rounded border border-gray-300 hover:bg-gray-200 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSkip}
          disabled={currentQuestionIndex >= lessonData?.length - 1}
        >
          Skip
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="w-full bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <span
            className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
            onClick={() => setError(null)}
          >
            <svg
              className="fill-current h-6 w-6 text-red-500"
              role="button"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </span>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center w-full">
        <ErrorBoundary>
          {QuestionComponent ? (
            <QuestionComponent
              key={questionKey}
              questionData={currentQuestion.fields}
              onCorrectAnswer={handleNextQuestion}
              video={video}
              colorTheme={colorTheme}
              questionKey={questionKey}
            />
          ) : (
            <div className="text-gray-500 text-lg">
              This type is not handled yet.
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default React.memo(CallAllTypeFromHere);
